import React, { useState, useEffect } from 'react';
import { AlertCircle, Plus, Edit, Trash2, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getToken() {
  return localStorage.getItem('token');
}

async function api(endpoint, options = {}) {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`);
  }
  return response.json();
}

export function IssuesPage() {
  const [issues, setIssues] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
    status: 'open'
  });

  useEffect(() => {
    loadIssues();
  }, []);

  const loadIssues = async () => {
    try {
      const data = await api('/api/issues');
      setIssues(data);
    } catch (error) {
      toast.error('Failed to load issues');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIssue) {
        await api(`/api/issues/${editingIssue.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        toast.success('Issue updated');
      } else {
        await api('/api/issues', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        toast.success('Issue created');
      }
      setShowForm(false);
      setEditingIssue(null);
      setFormData({ title: '', description: '', priority: 'medium', assigned_to: '', status: 'open' });
      loadIssues();
    } catch (error) {
      toast.error('Failed to save issue');
    }
  };

  const handleEdit = (issue) => {
    setEditingIssue(issue);
    setFormData({
      title: issue.title,
      description: issue.description || '',
      priority: issue.priority,
      assigned_to: issue.assigned_to || '',
      status: issue.status
    });
    setShowForm(true);
  };

  const handleDelete = async (issueId) => {
    if (!window.confirm('Delete this issue?')) return;
    try {
      await api(`/api/issues/${issueId}`, { method: 'DELETE' });
      toast.success('Issue deleted');
      loadIssues();
    } catch (error) {
      toast.error('Failed to delete issue');
    }
  };

  const handleStatusChange = async (issueId, newStatus) => {
    try {
      await api(`/api/issues/${issueId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      toast.success('Status updated');
      loadIssues();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'low':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-red-100 text-red-800 border-red-300',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      resolved: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return styles[status] || styles.open;
  };

  const filterIssues = (status) => {
    if (!status || status === 'all') return issues;
    return issues.filter(issue => issue.status === status);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Issues</h1>
          <p className="text-muted-foreground">Track and manage issues</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} data-testid="new-issue-button">
          <Plus className="h-4 w-4 mr-2" />
          New Issue
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingIssue ? 'Edit Issue' : 'Create Issue'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief description of the issue"
                  required
                  data-testid="issue-title-input"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description..."
                  rows={3}
                  data-testid="issue-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                    <SelectTrigger data-testid="issue-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="issue-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Assigned To</Label>
                  <Input
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    placeholder="Name or email"
                    data-testid="issue-assigned-input"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" data-testid="save-issue-button">
                  {editingIssue ? 'Update Issue' : 'Create Issue'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowForm(false);
                  setEditingIssue(null);
                  setFormData({ title: '', description: '', priority: 'medium', assigned_to: '', status: 'open' });
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="issues-tab-all">
            All ({issues.length})
          </TabsTrigger>
          <TabsTrigger value="open" data-testid="issues-tab-open">
            Open ({filterIssues('open').length})
          </TabsTrigger>
          <TabsTrigger value="in_progress" data-testid="issues-tab-in-progress">
            In Progress ({filterIssues('in_progress').length})
          </TabsTrigger>
          <TabsTrigger value="resolved" data-testid="issues-tab-resolved">
            Resolved ({filterIssues('resolved').length})
          </TabsTrigger>
        </TabsList>

        {['all', 'open', 'in_progress', 'resolved'].map(status => (
          <TabsContent key={status} value={status} className="mt-6">
            <div className="grid grid-cols-1 gap-4">
              {filterIssues(status).map((issue) => (
                <Card key={issue.id} className="hover:shadow-md transition-shadow" data-testid="issue-card">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-2">
                          {getPriorityIcon(issue.priority)}
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{issue.title}</h3>
                            {issue.description && (
                              <p className="text-sm text-muted-foreground mt-1">{issue.description}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 text-sm">
                          <Badge className={getStatusBadge(issue.status)}>
                            {issue.status.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline">
                            Priority: {issue.priority}
                          </Badge>
                          {issue.assigned_to && (
                            <Badge variant="outline">
                              Assigned: {issue.assigned_to}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-1">
                        <Select
                          value={issue.status}
                          onValueChange={(value) => handleStatusChange(issue.id, value)}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs" data-testid="issue-status-change">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(issue)} data-testid="edit-issue-button">
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(issue.id)} data-testid="delete-issue-button">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filterIssues(status).length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No {status !== 'all' ? status.replace('_', ' ') : ''} issues</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
